'use client';

import Link from 'next/link';
import { useEffect, useId, useRef, useState, type FormEvent } from 'react';
import type { CountryOption } from '@/lib/waitlist/countries';
import { bff } from '@/lib/waitlist/client';
import {
  DEFAULT_COUNTRY,
  FIELD_NAMES,
  joinFieldErrors,
  joinSchema,
  type WaitlistFieldErrors,
  type WaitlistRoleId,
} from '@/lib/waitlist/schema';
import { ROUTES } from '@/lib/site';
import DetailsFields, { detailsPayload, errorId, fieldId, type DetailsValues } from './DetailsFields';
import { RESEND_COOLDOWN_MS } from './EmailRequestForm';
import { ErrorSummary, Honeypot, LiveMessage, errorClass, inputClass, labelClass, primaryButtonClass, secondaryButtonClass, waitCopy } from './ui';

type Status =
  | { kind: 'idle' }
  | { kind: 'submitting' }
  | { kind: 'limited'; retryAfter?: number }
  | { kind: 'unavailable'; message: string };

/**
 * The waitlist form.
 *
 * Native controls throughout so keyboard and screen-reader use work without
 * custom ARIA. Every value is controlled, so nothing typed is lost to an error.
 * Validation runs here for immediate feedback and again on the server and the
 * gateway, which are the authority. The success state is "check your inbox":
 * the gateway answers the same for new and existing addresses, and nothing is
 * confirmed until the person clicks the emailed link.
 */
export default function WaitlistForm({ defaultRole, countries }: { defaultRole?: WaitlistRoleId; countries: CountryOption[] }) {
  const prefix = useId().replace(/:/g, '');
  const [email, setEmail] = useState('');
  const [website, setWebsite] = useState('');
  const [details, setDetails] = useState<DetailsValues>({
    displayName: '',
    countryCode: DEFAULT_COUNTRY,
    region: '',
    city: '',
    roles: defaultRole ? [defaultRole] : [],
    services: [],
    marketing: false,
  });
  const [errors, setErrors] = useState<WaitlistFieldErrors>({});
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const [sentTo, setSentTo] = useState<string | null>(null);
  const summaryRef = useRef<HTMLDivElement>(null);
  const statusRef = useRef<HTMLDivElement>(null);

  const showErrors = (errs: WaitlistFieldErrors) => {
    setErrors(errs);
    requestAnimationFrame(() => summaryRef.current?.focus());
  };

  async function onSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (status.kind === 'submitting') return;
    const input = { email, ...detailsPayload(details), website: website || undefined };
    const parsed = joinSchema.safeParse(input);
    if (!parsed.success) {
      showErrors(joinFieldErrors(input, parsed.error));
      return;
    }

    setErrors({});
    setStatus({ kind: 'submitting' });
    const res = await bff<{ accepted: boolean }>('', { body: parsed.data });
    if (res.ok) {
      setStatus({ kind: 'idle' });
      setSentTo(parsed.data.email);
      return;
    }
    if (res.code === 'invalid' && res.fields && Object.keys(res.fields).length) {
      setStatus({ kind: 'idle' });
      showErrors(res.fields);
      return;
    }
    setStatus(res.status === 429 ? { kind: 'limited', retryAfter: res.retryAfter } : { kind: 'unavailable', message: res.error });
    requestAnimationFrame(() => statusRef.current?.focus());
  }

  if (sentTo) return <CheckInbox email={sentTo} />;

  const busy = status.kind === 'submitting';
  const summary = FIELD_NAMES.filter((f) => errors[f]).map((f) => ({ id: fieldId(prefix, f), message: errors[f] as string }));

  return (
    <form onSubmit={onSubmit} noValidate className="relative flex flex-col gap-5" aria-describedby={`${prefix}-privacy`}>
      <ErrorSummary ref={summaryRef} items={summary} titleId={`${prefix}-summary`} />

      <div className="flex flex-col gap-2">
        <label htmlFor={fieldId(prefix, 'email')} className={labelClass}>
          Email address
        </label>
        <input
          id={fieldId(prefix, 'email')}
          name="email"
          type="email"
          autoComplete="email"
          inputMode="email"
          required
          maxLength={254}
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          aria-invalid={errors.email ? true : undefined}
          aria-describedby={errors.email ? errorId(prefix, 'email') : undefined}
          className={inputClass}
        />
        {errors.email ? (
          <p id={errorId(prefix, 'email')} className={errorClass}>
            {errors.email}
          </p>
        ) : null}
      </div>

      <DetailsFields prefix={prefix} values={details} errors={errors} countries={countries} onChange={setDetails} />

      <Honeypot id={`${prefix}-website`} value={website} onChange={setWebsite} />

      <button type="submit" disabled={busy} className={`${primaryButtonClass} mt-1 w-full`}>
        {busy ? 'Submitting…' : 'Join the waitlist'}
      </button>

      <LiveMessage ref={statusRef} tone="error">
        {status.kind === 'limited'
          ? `Too many attempts from your connection. Please wait ${waitCopy(status.retryAfter)} and try again. Your details are still here.`
          : status.kind === 'unavailable'
            ? `${status.message} Your details are still here.`
            : null}
      </LiveMessage>

      <p id={`${prefix}-privacy`} className="text-[13px] leading-relaxed text-grey-600">
        We use your details to confirm your email, tell you when Point2 opens near you and how to get started. Product
        updates are separate and optional. See our{' '}
        <Link href={ROUTES.privacy} className="text-brand-700 underline underline-offset-2">
          Privacy Policy
        </Link>{' '}
        for how we store your details and how to leave or delete them.
      </p>
    </form>
  );
}

/** After a join: nothing is confirmed until the emailed link is opened. */
function CheckInbox({ email }: { email: string }) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const resendRef = useRef<HTMLDivElement>(null);
  const [resend, setResend] = useState<{ kind: 'idle' | 'busy' | 'sent' } | { kind: 'failed'; message: string }>({ kind: 'idle' });
  const [coolingDown, setCoolingDown] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);

  useEffect(() => {
    headingRef.current?.focus();
    return () => clearTimeout(timer.current);
  }, []);

  async function onResend() {
    if (resend.kind === 'busy' || coolingDown) return;
    setResend({ kind: 'busy' });
    const res = await bff<{ accepted: boolean }>('/resend', { body: { email } });
    if (res.ok) {
      setResend({ kind: 'sent' });
      setCoolingDown(true);
      timer.current = setTimeout(() => setCoolingDown(false), RESEND_COOLDOWN_MS);
    } else {
      setResend({
        kind: 'failed',
        message: res.status === 429 ? `Too many attempts. Please wait ${waitCopy(res.retryAfter)} and try again.` : res.error,
      });
    }
    requestAnimationFrame(() => resendRef.current?.focus());
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="rounded-lg bg-success-50 px-4 py-4 text-success-800">
        <h3 ref={headingRef} tabIndex={-1} className="text-lg font-semibold outline-none">
          Check your inbox
        </h3>
        <p className="mt-1 text-[15px] leading-relaxed">
          Check your inbox — we&apos;ve sent a link to confirm your email. Nothing more to do until then.
        </p>
      </div>
      <p className="text-[15px] leading-relaxed text-grey-700">
        Already on the waitlist? We&apos;ll send you a link to manage your details instead.
      </p>
      <div className="flex flex-col gap-3">
        <p className="text-[15px] font-medium text-grey-800">Didn&apos;t get it?</p>
        <button
          type="button"
          onClick={onResend}
          disabled={resend.kind === 'busy' || coolingDown}
          className={`${secondaryButtonClass} w-full sm:w-auto sm:self-start`}
        >
          {resend.kind === 'busy' ? 'Sending…' : 'Send the email again'}
        </button>
        <LiveMessage ref={resendRef} tone={resend.kind === 'failed' ? 'error' : 'info'}>
          {resend.kind === 'sent'
            ? "If that address is waiting to be confirmed, we've sent a new link. Emails can take a few minutes, so check your spam folder too. You can ask again in 2 minutes."
            : resend.kind === 'failed'
              ? resend.message
              : null}
        </LiveMessage>
      </div>
    </div>
  );
}
